import React, { useState, useEffect, useMemo } from 'react';
import { useCollection, useCollectionOnce } from 'react-firebase-hooks/firestore';
import { SxStyle } from '../../../types';
import Button from '@mui/material/Button';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditUserDialog from './components/edit-user-dialog';
import RemoveUserDialog from './components/remove-user-dialog';
import AddUserDialog from './components/add-user-dialog';
import firebaseApp from '../../../firebase-app';
import { FS_PATH_APP_CONFIGS, FS_PATH_BACKEND_AUTH_USERS } from '../../../constants';
import { removeInaccessibleUsersAndTeams } from './helpers';
import { useStringsContext } from '../../../contexts/strings';
import { SuperAdminUser } from '../../../types';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import ToggleSuperadminRow from './components/toggle-superadmin-row';
import DeleteUserRow from './components/remove-user-row';
import EditUserRow from './components/edit-user-row';
import { TeamMinimized } from './types';
import { collection } from 'firebase/firestore';
import { useUserAuth } from '../../../contexts/auth';
import ScenePage from '../../common/scene/scene-page';
import SceneHeader from '../../common/scene/scene-header';

const styles: SxStyle = {
  dataGridContainer: {
    height: 'calc(100% - 80px)',
    width: '100%',
    marginTop: 4,
  },
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
  buttonContainer: (theme) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(5),
  }),
};

function UsersAndPermissions() {
  const [dataGridRows, setDataGridRows] = useState<SuperAdminUser[] | null>(null);
  const [appTeams, setAppTeams] = useState<TeamMinimized[] | null>(null);
  const [isEditPermissionsDialogOpen, setIsEditPermissionsDialogOpen] = useState(false);
  const [isRemoveUserDialogOpen, setIsRemoveUserDialogOpen] = useState(false);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<SuperAdminUser | null>(null);
  const { strings } = useStringsContext();
  const auth = useUserAuth();

  const { isSuperAdmin } = auth;

  const [usersSnapshot, loadingUsers, errorUsers] = useCollection(
    collection(firebaseApp.db, FS_PATH_BACKEND_AUTH_USERS)
  );

  const [appConfigSnapshot, loadingAppConfig, errorAppConfig] = useCollectionOnce<
    Record<string, string>
  >(collection(firebaseApp.db, FS_PATH_APP_CONFIGS));

  useEffect(() => {
    if (appConfigSnapshot && !loadingAppConfig && !errorAppConfig) {
      const newAppTeams = appConfigSnapshot?.docs
        .filter((doc: any) => !doc.id.includes('-tablet') && !doc.id.includes('-mobile'))
        .map((doc: any) => doc.data())
        .filter((data) => data.appShortname)
        .map((data: any) => {
          return {
            name: data.name,
            abbrv: data.appShortname,
            league: data.configLeagueName,
            appType: data.appType,
          } as TeamMinimized;
        });
      setAppTeams(newAppTeams);
    }
  }, [appConfigSnapshot, errorAppConfig, loadingAppConfig]);

  useEffect(() => {
    if (auth.currentUser && usersSnapshot && !loadingUsers && !errorUsers) {
      const users = usersSnapshot.docs.map((users: any) => users.data()) || [];
      const accessibleUsers = removeInaccessibleUsersAndTeams(auth, users);
      setDataGridRows(accessibleUsers);
    }
  }, [auth, usersSnapshot, loadingUsers, errorUsers]);

  const handleTeamPermissionClose = () => {
    setIsEditPermissionsDialogOpen(false);
  };

  const handleRemoveUserClose = () => {
    setIsRemoveUserDialogOpen(false);
  };

  const handleEditUserPermissions = (editUser: SuperAdminUser) => {
    setEditUser(editUser);
    setIsEditPermissionsDialogOpen(true);
  };

  const handleRemoveUser = (editUser: SuperAdminUser) => {
    setEditUser(editUser);
    setIsRemoveUserDialogOpen(true);
  };

  const initialColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: strings.usersFields.users,
        flex: 1,
      },
      {
        field: 'superadmin',
        headerName: strings.usersFields.superadmin,
        flex: 1,
        maxWidth: 120,
        hide: !isSuperAdmin,
        renderCell: (params: GridRenderCellParams) => (
          <ToggleSuperadminRow params={params} currentUser={auth} />
        ),
      },
      {
        field: 'permissions',
        headerName: strings.usersFields.permissions,
        flex: 1,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <EditUserRow appTeams={appTeams!} params={params} />
        ),
      },
      {
        field: 'edit',
        headerName: strings.usersFields.edit,
        flex: 0,
        width: 110,
        align: 'center',
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <DeleteUserRow
            params={params}
            hideDelete={params.row.uid === auth.currentUser?.uid || !isSuperAdmin}
            onRemoveClick={handleRemoveUser}
            onEditClick={handleEditUserPermissions}
          />
        ),
      },
    ],
    [appTeams, auth, isSuperAdmin, strings]
  );

  return (
    <ScenePage title={strings.appBar.usersAndPermissions}>
      {!loadingUsers &&
        !errorUsers &&
        !loadingAppConfig &&
        !errorAppConfig &&
        dataGridRows &&
        appTeams && (
          <>
            <SceneHeader title={strings.appBar.usersAndPermissions}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsAddUserDialogOpen(true)}
                startIcon={<AddCircleIcon />}
              >
                {strings.usersAndPermissions.addUser}
              </Button>
            </SceneHeader>
            <Paper elevation={2} sx={styles.dataGridContainer}>
              <DataGrid
                sx={styles.dataGrid}
                rows={dataGridRows}
                columns={initialColumns}
                getRowId={(row) => row.uid}
                autoPageSize
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                initialState={{
                  sorting: {
                    sortModel: [
                      {
                        field: 'email',
                        sort: 'asc',
                      },
                    ],
                  },
                }}
              />
            </Paper>
            {isAddUserDialogOpen && (
              <AddUserDialog
                isOpen={isAddUserDialogOpen}
                onClose={() => setIsAddUserDialogOpen(false)}
              />
            )}
            {isEditPermissionsDialogOpen && (
              <EditUserDialog
                isOpen={isEditPermissionsDialogOpen}
                onClose={handleTeamPermissionClose}
                appTeams={appTeams}
                editUser={editUser!}
                currentUser={auth!}
              />
            )}
            {isRemoveUserDialogOpen && (
              <RemoveUserDialog
                isOpen={isRemoveUserDialogOpen}
                onClose={handleRemoveUserClose}
                selectedUser={editUser!}
              />
            )}
          </>
        )}
    </ScenePage>
  );
}

export default UsersAndPermissions;
